import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { RedlineAction, RedlineChange } from "work/proposals/redlining/changes/redline-change";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import FeeScheduleRedlineSchema, { FeeScheduleRedlineJSON } from "./_schema/fee-schedule-redline-schema";
import { FeeScheduleCategoryRedlineJSON } from "work/proposals/redlining/fee-schedule-redline/_schema/fee-schedule-category-redline-schema";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import _ from "lodash";

export class FeeScheduleRedline {
  public static fromJSONArray(arrayObject: FeeScheduleRedlineJSON) {
    const feeScheduleRedline = new FeeScheduleRedline([], []);
    arrayObject.redlines.forEach((redlineCategory: FeeScheduleCategoryRedlineJSON) => {
      const redlineInstance = FeeScheduleCategoryRedline.fromJSON(
        redlineCategory,
        ProposalField.FeeScheduleCategory(
          Guid.fromJSON(
            redlineCategory.originalEntry?.id ?? redlineCategory.revisedEntry?.id ?? redlineCategory.currentEntry?.id,
          ) ?? Guid.generate(),
        ),
      );
      if (redlineInstance) feeScheduleRedline._redlines.push(redlineInstance);
    });
    return feeScheduleRedline;
  }

  constructor(
    originalEntries: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    revisedEntries: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    currentEntries?: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
  ) {
    originalEntries.forEach((originalEntry) => {
      const revisedEntry = revisedEntries.find((revisedEntry) => revisedEntry.id?.isEqualTo(originalEntry.id)) ?? null;

      const currentEntry = currentEntries?.find((entry) => entry.id?.isEqualTo(originalEntry.id)) ?? revisedEntry;

      const feeScheduleRedline = new FeeScheduleCategoryRedline(
        originalEntry.id,
        originalEntry,
        revisedEntry,
        currentEntry,
      );
      this._redlines.push(feeScheduleRedline);
    });

    const addedEntriesInRevision = revisedEntries.filter(
      (revisedEntry) => !originalEntries.some((originalEntry) => revisedEntry.id?.isEqualTo(originalEntry.id)),
    );
    addedEntriesInRevision.forEach((revisionAddedEntry) => {
      const currentEntry = currentEntries?.find((currentEntry) => currentEntry.id?.isEqualTo(revisionAddedEntry.id));

      this._redlines.push(
        new FeeScheduleCategoryRedline(revisionAddedEntry.id, null, revisionAddedEntry, currentEntry),
      );
    });

    const addedEntriesInCurrent = currentEntries
      ? currentEntries.filter(
          (currentEntry) =>
            !originalEntries.some((originalEntry) => currentEntry.id?.isEqualTo(originalEntry.id)) &&
            !revisedEntries.some((revisedEntry) => currentEntry.id?.isEqualTo(revisedEntry.id)),
        )
      : [];

    addedEntriesInCurrent.forEach((currentAddedEntry) => {
      this._redlines.push(new FeeScheduleCategoryRedline(currentAddedEntry.id, null, null, currentAddedEntry));
    });
  }

  public get field(): ProposalField {
    return ProposalField.FeeSchedule;
  }

  public get redlines(): FeeScheduleCategoryRedline[] {
    return this._redlines;
  }

  public get templateIds(): Guid[] {
    return [...this._templateIds];
  }
  public get sessionHistory(): RedlineChange[] {
    return [...this._sessionHistory, ...this._redlines.flatMap((redline) => redline.sessionHistory)];
  }

  public get isRevised(): boolean {
    return this._redlines.some((redlineCategory) => redlineCategory.isRevised);
  }

  public get isResolved(): boolean {
    return this._redlines.every((redlineCategory) => redlineCategory.isResolved);
  }

  public get canBeUndone(): boolean {
    return this._redlines.some((redlineCategory) => redlineCategory.canBeUndone);
  }

  public get isEmpty(): boolean {
    return this._redlines.every((redlineCategory) => redlineCategory.currentEntry === null);
  }

  public get hasRejections(): boolean {
    return this._redlines.some((redline) => redline.isRejected);
  }

  public get hasChanges(): boolean {
    return this._redlines.some((redline) => redline.hasChanges);
  }

  public get currentEntries(): (FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency)[] {
    return this._redlines
      .map((redline) => redline.currentEntry)
      .filter((entry): entry is FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency => !!entry);
  }

  public clearSessionHistory() {
    this._sessionHistory = [];
    this._redlines.forEach((redline) => redline.clearSessionHistory());
  }

  public addEntry(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const existingCategoryIndex = newFeeScheduleRedline._redlines.findIndex((redlineCategory) =>
      redlineCategory.field.id?.isEqualTo(category.id),
    );
    if (existingCategoryIndex >= 0) {
      newFeeScheduleRedline._redlines[existingCategoryIndex] =
        newFeeScheduleRedline._redlines[existingCategoryIndex].edit(category);
    } else {
      let newCategoryRedline = new FeeScheduleCategoryRedline(category.id, null, null);
      newCategoryRedline = newCategoryRedline.edit(category);
      newCategoryRedline.clearSessionHistory();

      newFeeScheduleRedline._redlines.push(newCategoryRedline);
      newFeeScheduleRedline._lastEntryUpdated = category.id;

      newFeeScheduleRedline._sessionHistory.push(
        new RedlineChange({
          field: newCategoryRedline.field,
          isResolved: true,
          action: RedlineAction.Add,
          textChanges: newCategoryRedline.textChanges,
        }),
      );
    }
    return newFeeScheduleRedline;
  }

  public getMatchingCategoryRedline(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ): FeeScheduleCategoryRedline | null {
    if (!category.name) return null;
    return (
      this._redlines.find(
        (redlineCategory) =>
          redlineCategory.field.id?.isEqualTo(category.id) ||
          [
            redlineCategory.originalEntry?.name.toString(),
            redlineCategory.revisedEntry?.name.toString(),
            redlineCategory.currentEntry?.name.toString(),
          ]
            .filter(Boolean)
            .includes(category.name?.toString()),
      ) ?? null
    );
  }

  public replaceEntryById(
    fieldId: Guid,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const targetIndex = newFeeScheduleRedline._redlines.findIndex((existingCategory) =>
      existingCategory.field.id?.isEqualTo(fieldId),
    );
    if (targetIndex === -1) throw new Error("Could not find category to update");
    const targetCategory = newFeeScheduleRedline._redlines[targetIndex];
    newFeeScheduleRedline._redlines[targetIndex] = targetCategory.edit(category);
    newFeeScheduleRedline._lastEntryUpdated = fieldId;
    return newFeeScheduleRedline;
  }

  public removeEntryByFieldId(categoryId: Guid): FeeScheduleRedline {
    if (!categoryId) throw new Error("No id provided");
    const newFeeScheduleRedline = this.clone();
    const targetIndex = newFeeScheduleRedline._redlines.findIndex((category) =>
      category.field.id?.isEqualTo(categoryId),
    );
    if (targetIndex === -1) throw new Error("Category not found");
    const targetCategoryRedline = newFeeScheduleRedline._redlines[targetIndex];

    newFeeScheduleRedline._sessionHistory = newFeeScheduleRedline._sessionHistory.filter(
      (change) => !change.field?.id?.isEqualTo(categoryId),
    );

    if (targetCategoryRedline.isNewlyAdded && targetCategoryRedline.isResolved) {
      newFeeScheduleRedline._redlines.splice(targetIndex, 1);
    } else {
      newFeeScheduleRedline._redlines[targetIndex] = targetCategoryRedline.remove();
      newFeeScheduleRedline._lastEntryUpdated = categoryId;
    }

    return newFeeScheduleRedline;
  }

  public acceptAll(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedline = newRedline.acceptRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  public acceptRedlineById(feeScheduleCategoryId: Guid): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const target = newFeeScheduleRedline._redlines.find((redlineFee) =>
      redlineFee.field.id?.isEqualTo(feeScheduleCategoryId),
    );

    if (target) {
      const index = newFeeScheduleRedline._redlines.indexOf(target);
      newFeeScheduleRedline._redlines[index] = target.accept();
      newFeeScheduleRedline._lastEntryUpdated = feeScheduleCategoryId;
    }
    return newFeeScheduleRedline;
  }

  public rejectAll(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id) {
        newRedline = newRedline.rejectRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  public rejectRedlineById(feeScheduleCategoryId: Guid): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const target = newFeeScheduleRedline._redlines.find((redlineFee) =>
      redlineFee.field.id?.isEqualTo(feeScheduleCategoryId),
    );

    if (target) {
      const index = newFeeScheduleRedline._redlines.indexOf(target);
      newFeeScheduleRedline._redlines[index] = target.reject();
      newFeeScheduleRedline._lastEntryUpdated = feeScheduleCategoryId;
    }
    return newFeeScheduleRedline;
  }

  public undoAll(): FeeScheduleRedline {
    let newRedline = this.clone();
    for (const redline of this._redlines) {
      if (redline.field.id && redline.canBeUndone) {
        newRedline = newRedline.undoRedlineById(redline.field.id);
      }
    }
    return newRedline;
  }

  public undoRedlineById(feeScheduleCategoryId: Guid): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    newFeeScheduleRedline._sessionHistory = [];

    const target = newFeeScheduleRedline._redlines.find((redlineCategory) =>
      redlineCategory.field.id?.isEqualTo(feeScheduleCategoryId),
    );

    if (target?.isNewlyAdded && !target.originalEntry) {
      newFeeScheduleRedline._redlines.splice(newFeeScheduleRedline._redlines.indexOf(target), 1);
      const redlineChange = new RedlineChange({
        field: this.field,
        isResolved: true,
        action: RedlineAction.Remove,
        textChanges: target.textChangeCollection.textChanges,
      });
      this._sessionHistory.push(redlineChange);
    } else if (target) {
      const index = newFeeScheduleRedline._redlines.indexOf(target);
      newFeeScheduleRedline._redlines[index] = target.undo();
    }
    return newFeeScheduleRedline;
  }

  public updateRedline(newFeeScheduleCategoryRedline: FeeScheduleCategoryRedline): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    const targetCategory = newFeeScheduleRedline._redlines.find((existingCategory) =>
      existingCategory.field.id?.isEqualTo(newFeeScheduleCategoryRedline.field.id),
    );
    if (!targetCategory) {
      console.warn("Could not find category to update");
      return newFeeScheduleRedline;
    }
    const targetIndex = newFeeScheduleRedline._redlines.findIndex((existingCategory) =>
      existingCategory.field.id?.isEqualTo(newFeeScheduleCategoryRedline.field.id),
    );
    newFeeScheduleRedline._redlines[targetIndex] = newFeeScheduleCategoryRedline;
    newFeeScheduleRedline._lastEntryUpdated = newFeeScheduleCategoryRedline.field.id;
    return newFeeScheduleRedline;
  }

  public updateTemplateIds(newTemplateIds: Guid[]): FeeScheduleRedline {
    const newFeeScheduleRedline = this.clone();
    newFeeScheduleRedline._templateIds = [...newTemplateIds];
    return newFeeScheduleRedline;
  }

  public isEqualTo(other: FeeScheduleRedline): boolean {
    if (this._redlines.length !== other._redlines.length) return false;
    if (this.templateIds.length !== other.templateIds.length) return false;
    return (
      _.every(this._redlines, (redline) => other._redlines.some((otherRedline) => redline.isEqualTo(otherRedline))) &&
      _.every(this.templateIds, (id) => other.templateIds.some((otherId) => id.isEqualTo(otherId)))
    );
  }

  public clone(): FeeScheduleRedline {
    const jsonCategories = FeeScheduleRedlineSchema.serialize(this);
    return FeeScheduleRedline.fromJSONArray(jsonCategories);
  }

  public toJSON(): FeeScheduleRedlineJSON {
    return FeeScheduleRedlineSchema.serialize(this);
  }

  protected _lastEntryUpdated: Guid | undefined;
  protected _redlines: FeeScheduleCategoryRedline[] = [];
  protected _templateIds: Guid[] = [];
  private _sessionHistory: RedlineChange[] = [];
}
